"use client";

import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";

import { cn } from "../../handlers/cn.utils";
import { Button } from "./button";

export function Modal({ isOpen, onClose, children, title, description, className, titleClassName }) {
	const [mounted, setMounted] = React.useState(false);

	React.useEffect(() => {
		setMounted(true);
		return () => setMounted(false);
	}, []);

	const handleBackdropClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	React.useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === "Escape") {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener("keydown", handleKeyDown);
		}

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [isOpen, onClose]);

	const modalContent = (
		<AnimatePresence>
			{isOpen && (
				<div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={handleBackdropClick}>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="absolute inset-0 bg-black/50"
					/>
					<motion.div
						initial={{ opacity: 0, scale: 0.95 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.95 }}
						className={cn(
							"relative z-[101] w-full max-w-2xl rounded-lg bg-white shadow-lg flex flex-col overflow-hidden",
							className,
						)}
						style={{ maxHeight: "calc(100vh - 32px)" }}
					>
						{(title || description) && (
							<div className="flex-none border-b px-6 py-4">
								<div className={cn("flex items-start justify-between", titleClassName)}>
									<div>
										{title && <h2 className="text-lg font-semibold">{title}</h2>}
										{description && <p className="mt-1 text-sm text-gray-700">{description}</p>}
									</div>
									<Button
										type="button"
										variant="ghost"
										className="h-9 w-9 p-0 text-gray-500 hover:text-gray-600"
										onClick={onClose}
									>
										<X className="h-4 w-4" />
									</Button>
								</div>
							</div>
						)}
						<div className="flex-1 overflow-y-auto min-h-0">{children}</div>
					</motion.div>
				</div>
			)}
		</AnimatePresence>
	);

	if (!mounted) return null;

	return createPortal(modalContent, document.body);
}
